import React, { useState, useEffect, useRef } from 'react';
import { Box, Typography, Stack, Button, IconButton, LinearProgress, Fade, Grow } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import HandshakeIcon from '@mui/icons-material/Handshake';
import PersonIcon from '@mui/icons-material/Person';
import BusinessIcon from '@mui/icons-material/Business';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import EventIcon from '@mui/icons-material/Event';
import EmailIcon from '@mui/icons-material/Email';
import NoteIcon from '@mui/icons-material/Note';
import PhoneIcon from '@mui/icons-material/Phone';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { glassCard, ACCENT, STATUS_WON, TEXT_PRIMARY, TEXT_SECONDARY, noMotion } from '../../designsystem/tokens';

const ENTITY_ICONS = {
    deal: <HandshakeIcon sx={{ fontSize: 20, color: STATUS_WON }} />,
    contact: <PersonIcon sx={{ fontSize: 20, color: STATUS_WON }} />,
    company: <BusinessIcon sx={{ fontSize: 20, color: STATUS_WON }} />,
    task: <TaskAltIcon sx={{ fontSize: 20, color: STATUS_WON }} />,
    meeting: <EventIcon sx={{ fontSize: 20, color: STATUS_WON }} />,
    mail: <EmailIcon sx={{ fontSize: 20, color: STATUS_WON }} />,
    note: <NoteIcon sx={{ fontSize: 20, color: STATUS_WON }} />,
    call: <PhoneIcon sx={{ fontSize: 20, color: STATUS_WON }} />,
};

const ENTITY_ROUTES = {
    deal: '/app/deals',
    contact: '/app/contacts',
    company: '/app/companies',
    task: '/app/tasks',
};

const AUTO_DISMISS_MS = 8000;

const SuccessOverlay = ({ createdEntities = [], onRecordAnother, onDone }) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [progress, setProgress] = useState(100);
    const [paused, setPaused] = useState(false);
    const timerRef = useRef(null);
    const startTimeRef = useRef(Date.now());
    const remainingRef = useRef(AUTO_DISMISS_MS);

    useEffect(() => {
        if (paused) return;
        const totalForThisRun = remainingRef.current;
        startTimeRef.current = Date.now();
        const tick = () => {
            const elapsed = Date.now() - startTimeRef.current;
            const remaining = totalForThisRun - elapsed;
            if (remaining <= 0) {
                onDone();
                return;
            }
            setProgress((remaining / totalForThisRun) * 100);
            timerRef.current = requestAnimationFrame(tick);
        };
        timerRef.current = requestAnimationFrame(tick);
        return () => { if (timerRef.current) cancelAnimationFrame(timerRef.current); };
    }, [paused, onDone]);

    const handlePause = () => {
        if (!paused) {
            const elapsed = Date.now() - startTimeRef.current;
            remainingRef.current = Math.max(0, remainingRef.current - elapsed);
        }
        setPaused(true);
    };

    const handleEntityTap = (entity) => {
        const route = ENTITY_ROUTES[entity.type];
        if (route && entity.id) {
            navigate(`${route}/${entity.id}`);
        }
    };

    return (
        <Fade in={true}>
            <Box
                data-testid="success-overlay"
                role="status"
                aria-live="polite"
                onClick={handlePause}
                sx={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    zIndex: 1300,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: 'rgba(0,0,0,0.6)',
                    backdropFilter: 'blur(8px)',
                    WebkitBackdropFilter: 'blur(8px)',
                    px: 2,
                }}
            >
                <Box
                    sx={{
                        ...glassCard,
                        maxWidth: 400,
                        width: '100%',
                        p: 3,
                        position: 'relative',
                        border: `1px solid ${STATUS_WON}30`,
                    }}
                    onClick={(e) => e.stopPropagation()}
                >
                    {/* Close button */}
                    <IconButton
                        onClick={onDone}
                        size="small"
                        sx={{ position: 'absolute', top: 8, right: 8, color: TEXT_SECONDARY }}
                        aria-label={t('close')}
                        data-testid="success-close"
                    >
                        <CloseIcon fontSize="small" />
                    </IconButton>

                    {/* Animated check */}
                    <Grow in={true} timeout={500}>
                        <Box sx={{ textAlign: 'center', mb: 2 }}>
                            <CheckCircleIcon sx={{ fontSize: 56, color: STATUS_WON }} />
                        </Box>
                    </Grow>

                    <Typography variant="h6" sx={{ color: TEXT_PRIMARY, textAlign: 'center', mb: 2, fontWeight: 600 }}>
                        {t('createdSuccessfully')}
                    </Typography>

                    {/* Entity list */}
                    {createdEntities.length > 0 && (
                        <Stack spacing={1} sx={{ mb: 2.5 }}>
                            {createdEntities.map((entity, i) => (
                                <Box
                                    key={i}
                                    data-testid={`created-entity-${entity.type}`}
                                    onClick={() => handleEntityTap(entity)}
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1.5,
                                        p: 1.5,
                                        borderRadius: '12px',
                                        backgroundColor: 'rgba(255,255,255,0.04)',
                                        cursor: entity.id ? 'pointer' : 'default',
                                        transition: 'background-color 200ms ease-out',
                                        '&:hover': entity.id ? { backgroundColor: 'rgba(255,255,255,0.08)' } : {},
                                        ...noMotion,
                                    }}
                                >
                                    {ENTITY_ICONS[entity.type] || ENTITY_ICONS.deal}
                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                        <Typography variant="caption" sx={{ color: TEXT_SECONDARY, textTransform: 'uppercase', fontSize: '0.68rem' }}>
                                            {t(entity.type)}
                                        </Typography>
                                        <Typography variant="body2" sx={{ color: TEXT_PRIMARY, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                            {entity.name || t(entity.type)}
                                        </Typography>
                                    </Box>
                                </Box>
                            ))}
                        </Stack>
                    )}

                    {/* Action buttons */}
                    <Stack direction="row" spacing={1.5}>
                        <Button
                            fullWidth
                            variant="contained"
                            data-testid="record-another-btn"
                            onClick={onRecordAnother}
                            sx={{
                                minHeight: 48,
                                borderRadius: '12px',
                                textTransform: 'none',
                                fontWeight: 600,
                                backgroundColor: ACCENT,
                                '&:hover': { backgroundColor: '#5e9294' },
                            }}
                        >
                            {t('recordAnother')}
                        </Button>
                        <Button
                            fullWidth
                            variant="outlined"
                            data-testid="success-done-btn"
                            onClick={onDone}
                            sx={{
                                minHeight: 48,
                                borderRadius: '12px',
                                textTransform: 'none',
                                fontWeight: 600,
                                color: TEXT_PRIMARY,
                                borderColor: 'rgba(255,255,255,0.2)',
                                '&:hover': { borderColor: 'rgba(255,255,255,0.4)' },
                            }}
                        >
                            {t('done')}
                        </Button>
                    </Stack>

                    {/* Auto-dismiss progress */}
                    {!paused && (
                        <LinearProgress
                            variant="determinate"
                            value={progress}
                            sx={{
                                mt: 2,
                                height: 3,
                                borderRadius: 2,
                                backgroundColor: 'rgba(255,255,255,0.08)',
                                '& .MuiLinearProgress-bar': {
                                    backgroundColor: STATUS_WON,
                                    transition: 'none',
                                },
                            }}
                        />
                    )}
                </Box>
            </Box>
        </Fade>
    );
};

export default SuccessOverlay;
